import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Icon,
  Stack,
  Box,
  Button,
  TextField,
} from '@nextiva/next-ui-components';
import { Avatar } from '@nextiva/next-ui-containers';
import { useGetUserInfoAvatar } from 'hooks/useGetUserInfoAvatar/useGetUserInfoAvatar';
import { RegistrationPendingProps } from 'interface/WizardV3';
import { DATE_FORMATS, formatDate } from 'utils/date-helpers';
import { downloadEnablementCsv } from 'utils/downloadEnablementCsv';
import { getTextRegistration } from 'utils/getTextRegistrationStatus';
import { getSubmissionDate } from 'utils/getSubmissionDate';
import { calculateFrom2FADate } from 'utils/calculateFrom2FADate';
import { CompanyTypeCell } from '../../CompanyTypeCell';
import { useWizardContextV3 } from '../../CampaignDialog/WizardContextV3/WizardContextV3';
import { ButtonDownload } from '../../MessagingNew.styled';
import { CompanyTypeInfo, SP2FAResubmit } from '../../CampaignDialog/constants';
import { OTPDialog } from '../../OTPDialog/OTPDialog';
import { usePostResend2FAEmail } from '../../CampaignDialog/WizardContextV3/usePostResend2FAEmail';
import { usePostResendOtp } from '../../CampaignDialog/WizardContextV3/usePostResendOtp';
import { Subtitle3Typo } from '../../CampaignDialog/WizardV3.styled';
import { RegistrationPendingAlert } from './RegistrationPendingAlert';

const dataTestId = 'PlatformAdminUI-Messaging-RegistrationPending';
export function RegistrationPending({
  smsCampaignData,
}: RegistrationPendingProps) {
  const [openOTPDialog, setOpenOTPDialog] = useState(false);
  const { enablementInfo } = useWizardContextV3();
  const { userInfo, isNotLoading } = useGetUserInfoAvatar(enablementInfo);
  const isSoleProprietor =
    smsCampaignData?.corpAcctType === CompanyTypeInfo[4].value;
  const isPublic = smsCampaignData?.corpAcctType === CompanyTypeInfo[1].value;
  const isBrandVerified = !!smsCampaignData?.campaignId;
  const hasBrandId = !!smsCampaignData?.brandId;
  const authVettingStatus = smsCampaignData?.authVettingStatus;
  const isAuthVettingNull = authVettingStatus === null;
  const isAuthVettingActive = authVettingStatus === 'ACTIVE';
  const isAuthVettingPending = authVettingStatus === 'PENDING';
  const dateTime = new Date();
  const submissionDate = getSubmissionDate({ smsCampaignData, enablementInfo });
  const text = getTextRegistration(isPublic, authVettingStatus);
  const {
    email2faSent,
    has7DaysPassed,
    has30DaysPassed,
    email2faExpiresIn,
    verificationExpiresIn,
  } = calculateFrom2FADate({ smsCampaignData });
  const deltaSubmissionDate = dateTime.getTime() - submissionDate.getTime();
  const has24HoursPassed = deltaSubmissionDate > 24 * 60 * 60 * 1000;
  const resend2FA = isAuthVettingPending && has7DaysPassed;
  const showEmail2faExpiresIn =
    isAuthVettingPending && email2faSent && !has7DaysPassed;
  const showResendOTP =
    isSoleProprietor && hasBrandId && !isBrandVerified && has24HoursPassed;
  const postResendOTPPhone = usePostResendOtp();
  const postResend2FAEmail = usePostResend2FAEmail();
  let resubmit = null;
  let resubmitValue = null;
  if (isPublic && !isBrandVerified && isAuthVettingPending) {
    resubmitValue = enablementInfo?.businessEmail;
  }
  if (showResendOTP) {
    resubmit = SP2FAResubmit;
    resubmitValue = enablementInfo?.mobilePhoneNumber;
  }
  const [disableResend2FAEmail, setDisableResend2FAEmail] = useState(false);

  return (
    <Grid container rowSpacing={3} data-testid={dataTestId}>
      <Grid container xs={12}>
        <Typography variant="subtitle2" color="#0B1029">
          Latest submission
        </Typography>
      </Grid>
      <RegistrationPendingAlert
        isPublic={isPublic}
        isSoleProprietor={isSoleProprietor}
        isBrandVerified={isBrandVerified}
        isAuthVettingActive={isAuthVettingActive}
        has24HoursPassed={has24HoursPassed}
      />
      {isNotLoading && (
        <Grid
          container
          item
          columnSpacing={6}
          columns={15}
          data-testid={`${dataTestId}-RegistrationInfo`}
        >
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Status:
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Icon icon={text.icon} size={16} sx={{ color: '#7A7D8C' }} />
              <Typography variant="body2" color="#0B1029">
                {text.status}
              </Typography>
            </Stack>
          </Grid>
          <CompanyTypeCell
            companyType={enablementInfo?.companyType}
            isApproved={false}
          />
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Submitted by:
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Avatar
                contact={userInfo}
                disablePresence
                avatarProps={{
                  size: 24,
                }}
              />
              <Typography variant="body2" color="#005FEC">
                {`${userInfo.firstName} ${userInfo.lastName}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Submission date:
            </Typography>
            <Typography variant="body2" color="#0B1029">
              {formatDate(submissionDate, DATE_FORMATS.SHORT)}
            </Typography>
          </Grid>
          <Grid item xs alignSelf="center" justifyItems="flex-end">
            <Box>
              <ButtonDownload
                variant="text"
                data-testid={`${dataTestId}-DownloadButton`}
                onClick={() => {
                  downloadEnablementCsv({
                    json: enablementInfo,
                    submissionDate: formatDate(
                      submissionDate,
                      DATE_FORMATS.SHORT,
                    ),
                    status: text.status,
                  });
                }}
              >
                Download
              </ButtonDownload>
            </Box>
          </Grid>
        </Grid>
      )}
      {isNotLoading &&
        isPublic &&
        (!isBrandVerified || isAuthVettingNull || isAuthVettingPending) && (
          <Grid
            container
            columns={15}
            sx={{
              backgroundColor: '#F7F8FA',
              padding: '16px',
              marginTop: '24px',
              borderRadius: '8px',
            }}
            data-testid={`${dataTestId}-VerificationDetails`}
          >
            <Grid item xs={showEmail2faExpiresIn ? 8 : 15} marginBottom={3}>
              <Typography variant="body2" color="#0B1029" fontWeight={800}>
                Verification details:
              </Typography>
            </Grid>
            {showEmail2faExpiresIn && (
              <Grid
                item
                xs={7}
                marginBottom={3}
                justifyItems="flex-end"
                alignSelf="center"
              >
                <Typography variant="body2" color="#0B1029">
                  Resend verification email after {Math.ceil(email2faExpiresIn)}{' '}
                  days.
                </Typography>
              </Grid>
            )}
            {!isBrandVerified && isAuthVettingNull && (
              <>
                <Grid item marginRight={3}>
                  <Typography
                    variant="body2"
                    color="#0B1029"
                    fontWeight={800}
                    marginBottom="8px"
                  >
                    Company name
                  </Typography>
                  <Typography variant="body2" color="#0B1029">
                    {enablementInfo?.companyLegalName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item marginRight={3}>
                  <Typography
                    variant="body2"
                    color="#0B1029"
                    fontWeight={800}
                    marginBottom="8px"
                  >
                    Brand name
                  </Typography>
                  <Typography variant="body2" color="#0B1029">
                    {enablementInfo?.brandName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item marginRight={3}>
                  <Typography
                    variant="body2"
                    color="#0B1029"
                    fontWeight={800}
                    marginBottom="8px"
                  >
                    Stock symbol
                  </Typography>
                  <Typography variant="body2" color="#0B1029">
                    {enablementInfo?.stockSymbol || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item marginRight={3}>
                  <Typography
                    variant="body2"
                    color="#0B1029"
                    fontWeight={800}
                    marginBottom="8px"
                  >
                    Stock exchange
                  </Typography>
                  <Typography variant="body2" color="#0B1029">
                    {enablementInfo?.stockExchange || 'N/A'}
                  </Typography>
                </Grid>
              </>
            )}
            {!isBrandVerified && !isAuthVettingNull && (
              <>
                <Grid item>
                  <Typography
                    variant="body2"
                    color="#0B1029"
                    fontWeight={800}
                    marginBottom="8px"
                  >
                    Business email address
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" color="#0B1029">
                      {resubmitValue}
                    </Typography>
                  </Box>
                </Grid>
                {isAuthVettingPending && email2faSent && !has30DaysPassed && (
                  <>
                    <Grid item marginLeft={3}>
                      <Typography
                        variant="body2"
                        color="#0B1029"
                        fontWeight={800}
                        marginBottom="8px"
                      >
                        Verification expires:
                      </Typography>
                      <Typography variant="body2" color="#C33000">
                        {verificationExpiresIn > 0
                          ? `In ${Math.ceil(verificationExpiresIn)} days`
                          : 'Expired'}
                      </Typography>
                    </Grid>
                    <Grid item xs justifyItems="flex-end" alignSelf="center">
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            !resend2FA ||
                            postResend2FAEmail.isLoading ||
                            disableResend2FAEmail
                          }
                          onClick={() => {
                            postResend2FAEmail.mutate(
                              {
                                corpAcctNumber: smsCampaignData?.corpAcct,
                                businessEmailNew: enablementInfo?.businessEmail,
                                restartAuthVettingStatus: false,
                              },
                              {
                                onSuccess: () => setDisableResend2FAEmail(true),
                              },
                            );
                          }}
                          data-testid={`${dataTestId}-ResendEmailButton`}
                          sx={{
                            '&&.MuiButton-sizeMedium': {
                              padding: '12px 16px',
                            },
                          }}
                        >
                          Resend verification email
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        )}
      {isSoleProprietor && !!resubmit && (
        <Grid container item rowSpacing={2} columnSpacing={2} alignItems="end">
          <Grid item xs={12}>
            <Subtitle3Typo>{resubmit.infoText}</Subtitle3Typo>
          </Grid>
          <Grid item>
            <TextField
              label={
                <Typography sx={{ textTransform: 'none' }} variant="body2">
                  {resubmit.label}
                </Typography>
              }
              placeholder={resubmitValue}
              inputProps={{
                disabled: true,
              }}
              sx={{
                '& svg': {
                  cursor: 'pointer',
                  fill: (theme) => theme.palette.primary.main,
                },
              }}
              iconProps={{
                onClick: () => setOpenOTPDialog(true),
                'data-testid': `${dataTestId}-Resubmit2FAInputButton`,
              }}
              data-testid={`${dataTestId}-Resubmit2FAInput`}
              icon="refresh"
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="text"
              onClick={() =>
                postResendOTPPhone.mutate(smsCampaignData?.corpAcct)
              }
              data-testid={`${dataTestId}-Resubmit2FAButton`}
              sx={{
                '&.MuiButton-sizeMedium': {
                  padding: '12px 18px',
                  height: '40px',
                  width: '80px',
                },
              }}
            >
              {resubmit.buttonText}
            </Button>
          </Grid>
        </Grid>
      )}
      <OTPDialog
        corpAcctNumber={enablementInfo.corpAcctNbr}
        open={openOTPDialog}
        variant="changePhone"
        onClose={() => setOpenOTPDialog(false)}
        values={resubmitValue}
        data-testid={`${dataTestId}-OTPDialog`}
        setDisableButton={setDisableResend2FAEmail}
      />
    </Grid>
  );
}
